import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  fetchCompanySubContractDetail,
  fetchSubContractDetail,
  fetchSubContractStateSetting,
} from '../../../api/working';
import { OnOffButton } from '../../../components/items/on.off.button';
import { AlertModal } from '../../../components/modal/alert.modal';
import { useRecoilValue } from 'recoil';
import { authState } from '../../../atoms/authState';

interface SubContractDetailResponseData {
  subcontractId: number;
  vehicle: string;
  phone: string;
  target: string;
  content: string;
  state: string;
}

const CommonJobSearchDetail = () => {
  const { id } = useParams<{ id: string }>();
  const auth = useRecoilValue(authState);

  const [detailData, setDetailData] = useState<SubContractDetailResponseData>();
  const [switchStates, setSwitchStates] = useState<boolean>(false);

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  // 모달
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const clickHandler = () => {
    history.push(`/working/update/${id}`);
  };

  useEffect(() => {
    const getSubContractDetail = async () => {
      let response: SubContractDetailResponseData | undefined; // response의 타입을 명시적으로 선언

      if (auth.userType === 'driver') {
        response = await fetchSubContractDetail(Number(id));
      } else if (auth.userType === 'company') {
        response = await fetchCompanySubContractDetail(Number(id));
      }

      setDetailData(response);

      // state 문자열을 불리언 값으로 변환하여 스위치 상태 설정
      if (response!.state === '수주 됨') {
        setSwitchStates(true);
      } else {
        setSwitchStates(false);
      }
    };

    getSubContractDetail();
  }, [id]);

  const toggleSwitch = async () => {
    const newState = !switchStates; // 새로운 스위치 상태
    setSwitchStates(newState);

    // 새로운 상태를 서버로 전송
    try {
      const newStateString = newState ? '수주 됨' : '수주 안됨';

      const response = await fetchSubContractStateSetting(
        Number(id),
        newStateString,
      );

      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
      } else {
        setModalMessage('일감 등록에 실패했습니다.');
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error('일감 등록 중 오류가 발생했습니다.', error);
      setModalMessage('일감 등록 중 오류가 발생했습니다.');
      setIsModalOpen(true);
    }
  };

  return (
    <Container>
      <div className="car-info">
        <ul>
          <li>
            <div className="label">차량 번호</div>
            <div className="value">{detailData?.vehicle}</div>
          </li>
          <li>
            <div className="label">휴대폰 번호</div>
            <div className="value">{detailData?.phone}</div>
          </li>
          <li>
            <div className="label">도착 날짜</div>
            <div className="value">
              <span className="date">{detailData?.target}</span>
            </div>
          </li>
        </ul>
      </div>
      <div className="box-item on-off-btn">
        <div className="title">수주 여부</div>
        {auth.userType === 'driver' ? (
          <div className="switch-box">
            <span>{switchStates ? '수주 됨' : '수주 안됨'}</span>
            <OnOffButton
              isOn={switchStates}
              toggleSwitch={() => toggleSwitch()}
            />
          </div>
        ) : (
          <div className="state-box">
            {switchStates ? (
              <span>수주 됨</span>
            ) : (
              <span className="not">수주 안됨</span>
            )}
          </div>
        )}
      </div>
      <div className="contents">
        <div className="title">내용</div>
        <div className="text-box">{detailData?.content}</div>
      </div>
      {auth.userType === 'driver' ? (
        <Btn onClick={clickHandler}>수정 하기</Btn>
      ) : null}

      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </Container>
  );
};

export default CommonJobSearchDetail;

const Container = styled.div`
  height: 100%;
  padding: 2.4rem 2rem 9.4rem;
  height: 100%;
  .car-info {
    padding: 2.4rem 2.4rem 2.8rem;
    border-radius: 1.6rem;
    box-shadow: 0 2px 5px 3px rgba(0, 0, 0, 0.05);
    position: relative;
    ul {
      display: flex;
      flex-direction: column;
      gap: 1.2rem;
      li {
        display: flex;
        gap: 2.4rem;
        align-items: center;
        .label {
          min-width: 5.6rem;
          font-size: 1.2rem;
          font-weight: 600;
          color: ${(props) => props.theme.textAAAColor};
          line-height: 1.6rem;
        }
        .value {
          font-size: 1.4rem;
          font-weight: 600;
          &.status {
            min-width: 4.2rem;
            color: ${(props) => props.theme.textWhiteColor};
            background-color: ${(props) => props.theme.btnBlueColor};
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0.4rem;
            font-size: 1.1rem;
            border-radius: 1.1rem;
          }
        }
      }
    }
  }
  .contents {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    margin-top: 2.6rem;
    height: calc(100% - 16rem);
    padding: 0 1.2rem;
    .title {
      font-size: 1.5rem;
      font-weight: 600;
    }
    .text-box {
      flex: 1;
      overflow: hidden;
      font-size: 1.4rem;
      font-weight: 400;
      line-height: 1.4;
      white-space: pre-line;
    }
  }
  .on-off-btn {
    padding: 0 1.2rem;
    margin-top: 2rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    .title {
      margin-bottom: 0;
      font-size: 1.5rem;
      font-weight: 600;
    }
    .switch-box {
      display: flex;
      align-items: center;
      gap: 0.8rem;
      span {
        font-size: 1.4rem;
      }
    }
    .state-box {
      span {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 6rem;
        height: 2.4rem;
        border: 1px solid ${(props) => props.theme.btnBlueColor};
        font-size: 1.3rem;
        font-weight: 500;
        color: ${(props) => props.theme.btnBlueColor};
        &.not {
          color: ${(props) => props.theme.gray900};
          border: 1px solid ${(props) => props.theme.gray900};
        }
      }
    }
  }
`;

const Btn = styled.button`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  min-height: 9.4rem;
  border: none;
  outline: none;
  padding: 1.7rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-color: ${(props) => props.theme.btnStrongBlueColor};
  color: ${(props) => props.theme.textWhiteColor};
  font-size: 1.8rem;
  font-weight: 600;
`;
